import { useEffect, useRef } from 'react';

/**
 * THE NEXT PAGE ARRIVES BY SCROLLING, NOT BY A BUTTON. A *Load more* is a control
 * standing between somebody and the next row of a thing they are already scrolling
 * through, and on a phone it is a control they have to hit with a thumb at the exact
 * moment the list ends.
 *
 * ONE COPY, FOR THE REASON `SearchField` IS ONE COPY. This was written for the GIF
 * picker's grid (docs/5 § D260805-1221) and the room directory needed the same thing a
 * day later (§ D260805-1500) — two lists, one behaviour, and the parts that are easy
 * to get subtly different are exactly the parts that matter: the ref indirection
 * below, the margin, and the `enabled` guard. A second hand-written observer would
 * have paged twice as fast or not at all, and it would have taken a week to notice
 * which.
 *
 * `root` IS OPTIONAL, AND THE DEFAULT IS THE RIGHT ONE MORE OFTEN. A sheet with its
 * own scroller passes it; a list in the app's main column does not need to, because
 * an `IntersectionObserver` against the viewport already accounts for every scrolling
 * ancestor clipping the sentinel — so the row only counts as reached when it is
 * genuinely on screen, without this component having to be told which element
 * scrolls.
 */
export function More({
  root,
  enabled,
  onReach,
}: {
  root?: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  onReach: () => void;
}) {
  const sentinel = useRef<HTMLDivElement>(null);

  /* THROUGH A REF SO THE OBSERVER IS NOT REBUILT EVERY RENDER. The callback is a
     fresh closure on each pass; in the dependency array it would tear down and
     re-create the observer on every keystroke, and an observer created while the
     sentinel is already in view fires immediately. */
  const reach = useRef(onReach);
  reach.current = onReach;

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !enabled) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) reach.current();
      },
      /* A screen early, so the next page is usually there before the scroll
         reaches the end of this one. */
      { root: root?.current ?? null, rootMargin: '600px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, root]);

  return <div ref={sentinel} aria-hidden className="h-px" />;
}
